import { closeDialog } from "../core/dialogManager.js";
import { toast } from "../../../../layout/app-toast/utils/toastManager.js";
import { selectDialogDropdowns } from "../dropdowns/dropdownManager.js";
import { apiFetch } from "../../../../../app/apiClient.js";
import { dashboardModules, permittedLanguageNames } from "../../../../../app/utils/enums.js";
import { getContent } from "../../../app-table/tableManager/tableManager.js";
import { createCustomer } from "../../../app-table/tableManager/customerManager.js";
import { currentContext } from "../core/dialogContext.js";
import { chainSetup, onCreate } from "../core/dialogHandlerUtils.js";
import { setupLanguageDropdown, setupDashboardModulesDropdown, setupPermittedLanguagesDropdown } from "../dropdowns/dropdownInits.js";
import { renderItemCards } from "../core/dialogBuilder.js";
import { excludedCatalogIds } from "./itemManagerHandlers.js";

const WIZARD_STEP_KEY = 'wizard_createCustomer_step';
const WIZARD_APPS_KEY = 'wizard_createCustomer_apps';
const WIZARD_SERVICES_KEY = 'wizard_createCustomer_services';

function loadStoredList(key) {
    try {
        const raw = sessionStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : null;
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function clearWizardStorage() {
    sessionStorage.removeItem(WIZARD_STEP_KEY);
    sessionStorage.removeItem(WIZARD_APPS_KEY);
    sessionStorage.removeItem(WIZARD_SERVICES_KEY);
}

function saveWizardApps() {
    sessionStorage.setItem(WIZARD_APPS_KEY, JSON.stringify([...currentContext.wizardApps.toAdd.values()]));
}

function saveWizardServices() {
    sessionStorage.setItem(WIZARD_SERVICES_KEY, JSON.stringify([...currentContext.wizardServices.toAdd.values()]));
}

function markWizardFieldError(element) {
    if (typeof element.setError === 'function') {
        element.setError();
    } else {
        element.style.borderColor = 'red';
        element.addEventListener('input', () => { element.style.borderColor = ''; }, { once: true });
    }
}

function validateWizardStep(stepEl) {
    if (!stepEl) return true;
    const missing = [...stepEl.querySelectorAll('[data-required]')].filter(element => {
        const value = typeof element.getValue === 'function' ? element.getValue() : element.value;
        return !value || (Array.isArray(value) ? !value.length : String(value).trim() === '');
    });
    missing.forEach(markWizardFieldError);
    return missing.length === 0;
}

export function buildCreateCustomerHandlers(data) {
    const totalSteps = 6;
    let currentStep = 0;
    let isRestoring = false;

    currentContext.wizardApps     = { allApps: [], toAdd: new Map(), draft: null, listEl: null, row: null };
    currentContext.wizardServices = { allServices: [], toAdd: new Map(), draft: null, listEl: null, row: null };

    function renderWizardApps() {
        const ctx = currentContext.wizardApps;
        const items = [...ctx.toAdd.values(), ...(ctx.draft ? [ctx.draft] : [])];
        renderItemCards(ctx.listEl, items, ctx.row, ctx.draft);
        saveWizardApps();
        refreshSummaryIfVisible();
    }

    function renderWizardServices() {
        const ctx = currentContext.wizardServices;
        const items = [...ctx.toAdd.values(), ...(ctx.draft ? [ctx.draft] : [])];
        renderItemCards(ctx.listEl, items, ctx.row, ctx.draft);
        saveWizardServices();
        refreshSummaryIfVisible();
    }

    function renderWizardSummary(content) {
        const container = content.querySelector('#wizardSummaryContainer');
        if (!container) return;
        container.innerHTML = '';

        const name       = content.querySelector('#inputCustomerName')?.getValue?.() ?? '';
        const shortName  = content.querySelector('#inputCustomerShortName')?.getValue?.() ?? '';
        const language   = content.querySelector('#inputCustomerLanguage')?.getValue?.();
        const moduleIds  = content.querySelector('#inputCustomerDashboardModules')?.getValue?.() ?? [];
        const languageIds = content.querySelector('#inputCustomerPermittedLanguages')?.getValue?.() ?? [];

        const sections = [
            { step: 0, title: 'Stammdaten', full: true, lines: [
                `Name: ${name || '—'}`,
                `Kurzname: ${shortName || '—'}`,
                `Sprache: ${permittedLanguageNames[language] ?? '—'}`,
            ]},
            { step: 1, title: 'Applikationen',
                lines: [...currentContext.wizardApps.toAdd.values()].map(app => app.url ? `${app.name} (${app.url})` : app.name) },
            { step: 2, title: 'Services',
                lines: [...currentContext.wizardServices.toAdd.values()].map(service => service.isTranslationActive ? `${service.name} (Übersetzung aktiv)` : service.name) },
            { step: 3, title: 'Dashboard Module',
                lines: moduleIds.map(id => dashboardModules[id]).filter(Boolean) },
            { step: 4, title: 'Erlaubte Sprachen',
                lines: languageIds.map(id => permittedLanguageNames[id]).filter(Boolean) },
        ];

        sections.forEach(section => {
            const sectionEl = document.createElement('div');
            sectionEl.className = section.full ? 'wizardSummarySection wizardSummarySection--full' : 'wizardSummarySection';

            const header = document.createElement('div');
            header.className = 'wizardSummaryHeader';
            const title = document.createElement('p');
            title.className = 'wizardSummaryTitle';
            title.textContent = section.title;
            header.appendChild(title);

            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'wizardSummaryEditBtn';
            editBtn.title = 'Zu diesem Schritt springen';
            editBtn.innerHTML = '<i class="fa-solid fa-pen"></i>';
            editBtn.addEventListener('click', () => goToStep(section.step));
            header.appendChild(editBtn);
            sectionEl.appendChild(header);

            if (section.lines.length === 0) {
                const empty = document.createElement('p');
                empty.className = 'wizardSummaryEmpty';
                empty.textContent = 'Keine Angaben';
                sectionEl.appendChild(empty);
            } else {
                section.lines.forEach(line => {
                    const lineEl = document.createElement('p');
                    lineEl.className = 'wizardSummaryLine';
                    lineEl.textContent = line;
                    sectionEl.appendChild(lineEl);
                });
            }

            container.appendChild(sectionEl);
        });
    }

    function goToStep(index) {
        currentStep = index;
        sessionStorage.setItem(WIZARD_STEP_KEY, String(index));
        const content = getContent();
        if (!content) return;

        content.querySelectorAll('.wizardStep').forEach(el => {
            el.classList.toggle('active', Number(el.dataset.step) === index);
        });
        content.querySelectorAll('.wizardProgressStep').forEach(el => {
            const step = Number(el.dataset.step);
            el.classList.toggle('active', step === index);
            el.classList.toggle('completed', step < index);
        });

        const backBtn = content.querySelector('#wizardBackBtn');
        const nextBtn = content.querySelector('#wizardNextBtn');
        if (backBtn) backBtn.textContent = index === 0 ? 'Abbrechen' : 'Zurück';
        if (nextBtn) nextBtn.textContent = index === totalSteps - 1 ? 'Speichern' : 'Weiter';

        if (index === totalSteps - 1) renderWizardSummary(content);
    }

    function refreshSummaryIfVisible() {
        if (currentStep !== totalSteps - 1) return;
        const content = getContent();
        if (content) renderWizardSummary(content);
    }

    const handlers = {};
    return Object.assign(handlers, {
        initLanguageDropdown: (element) => chainSetup(handlers, setupLanguageDropdown(element, data)),
        initDashboardModulesDropdown: (element) => chainSetup(handlers, setupDashboardModulesDropdown(element, data)),
        initPermittedLanguagesDropdown: (element) => chainSetup(handlers, setupPermittedLanguagesDropdown(element, data)),

        wizardApplications: {
            onMount: async (container, row) => {
                const ctx = currentContext.wizardApps;
                ctx.listEl = container.querySelector('.appManagerList');
                ctx.row = row;
                const catalog = ((await apiFetch("Applications")) ?? []).map(app => ({ id: app.id, name: app.name }));
                ctx.allApps = catalog;

                const storedApps = isRestoring ? loadStoredList(WIZARD_APPS_KEY) : null;
                if (storedApps) {
                    storedApps.forEach(app => ctx.toAdd.set(String(app.id), app));
                } else {
                    (data?.templateApplications ?? []).forEach(templateApp => {
                        const app = catalog.find(a => String(a.id) === String(templateApp.applicationId));
                        if (app) ctx.toAdd.set(String(app.id), { id: app.id, name: app.name, url: templateApp.url ?? '' });
                    });
                }
                renderWizardApps();
            },
            onAddToggle: () => {
                const ctx = currentContext.wizardApps;
                if (ctx.draft) return;
                ctx.draft = {};
                renderWizardApps();
            },
            getFieldOptions: () => {
                const ctx = currentContext.wizardApps;
                const excluded = excludedCatalogIds(new Map(), new Set(), ctx.toAdd);
                return ctx.allApps.filter(app => !excluded.has(String(app.id)));
            },
            onConfirm: (item, values) => {
                const ctx = currentContext.wizardApps;
                const selectedId = String(values.id ?? '');
                if (!selectedId) return false;
                const url = String(values.url ?? '').trim();
                if (!url) { toast("Bitte eine URL eingeben.", "error"); return false; }
                const app = ctx.allApps.find(a => String(a.id) === selectedId);
                if (!app) return false;
                ctx.toAdd.set(selectedId, { id: app.id, name: app.name, url });
                ctx.draft = null;
                renderWizardApps();
                return true;
            },
            onRemove: (item) => {
                const ctx = currentContext.wizardApps;
                if (item === ctx.draft) { ctx.draft = null; renderWizardApps(); return; }
                ctx.toAdd.delete(String(item.id));
                renderWizardApps();
            },
        },

        wizardServices: {
            onMount: async (container, row) => {
                const ctx = currentContext.wizardServices;
                ctx.listEl = container.querySelector('.appManagerList');
                ctx.row = row;
                const catalog = ((await apiFetch("Services")) ?? []).map(service => ({ id: service.id, name: service.name }));
                ctx.allServices = catalog;

                const storedServices = isRestoring ? loadStoredList(WIZARD_SERVICES_KEY) : null;
                if (storedServices) {
                    storedServices.forEach(service => ctx.toAdd.set(String(service.id), service));
                } else {
                    (data?.templateServices ?? []).forEach(templateService => {
                        const service = catalog.find(s => String(s.id) === String(templateService.id));
                        if (service) ctx.toAdd.set(String(service.id), { id: service.id, name: service.name, isTranslationActive: !!templateService.isTranslationActive });
                    });
                }
                renderWizardServices();
            },
            onAddToggle: () => {
                const ctx = currentContext.wizardServices;
                if (ctx.draft) return;
                ctx.draft = {};
                renderWizardServices();
            },
            getFieldOptions: () => {
                const ctx = currentContext.wizardServices;
                const excluded = excludedCatalogIds(new Map(), new Set(), ctx.toAdd);
                return ctx.allServices.filter(s => !excluded.has(String(s.id)));
            },
            onConfirm: (item, values) => {
                const ctx = currentContext.wizardServices;
                const selectedId = String(values.id ?? '');
                if (!selectedId) return false;
                const service = ctx.allServices.find(s => String(s.id) === selectedId);
                if (!service) return false;
                ctx.toAdd.set(selectedId, { id: service.id, name: service.name, isTranslationActive: false });
                ctx.draft = null;
                renderWizardServices();
                return true;
            },
            onRemove: (item) => {
                const ctx = currentContext.wizardServices;
                if (item === ctx.draft) { ctx.draft = null; renderWizardServices(); return; }
                ctx.toAdd.delete(String(item.id));
                renderWizardServices();
            },
            onToggleTranslation: (item) => {
                item.isTranslationActive = !item.isTranslationActive;
                saveWizardServices();
            },
        },

        onWizardBack: () => {
            if (currentStep === 0) { closeDialog(); return; }
            goToStep(currentStep - 1);
        },
        onWizardNext: () => {
            if (currentStep < totalSteps - 1) {
                if (currentStep === 0) {
                    const stepEl = getContent()?.querySelector('.wizardStep[data-step="0"]');
                    if (!validateWizardStep(stepEl)) return;
                }
                goToStep(currentStep + 1);
                return;
            }
            handlers.onSave();
        },
        onWizardStepClick: (index) => {
            if (index === currentStep) return;
            if (index > 0) {
                const stepEl = getContent()?.querySelector('.wizardStep[data-step="0"]');
                if (!validateWizardStep(stepEl)) { goToStep(0); return; }
            }
            goToStep(index);
        },

        onSave: onCreate(async () => {
            const apps = [...currentContext.wizardApps.toAdd.values()].map(app => ({ applicationId: app.id, url: app.url ?? '' }));
            const services = [...currentContext.wizardServices.toAdd.values()].map(service => ({ id: service.id, isTranslationActive: !!service.isTranslationActive }));

            return await createCustomer(apps, services);
        }),

        restore: () => {
            isRestoring = true;
            const storedStep = Number(sessionStorage.getItem(WIZARD_STEP_KEY));
            if (Number.isInteger(storedStep) && storedStep > 0 && storedStep < totalSteps) goToStep(storedStep);
        },
        onClose: clearWizardStorage,

        setup: data ? () => selectDialogDropdowns(data) : undefined,
    });
}